import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  archiveContact,
  createContact,
  listContacts,
  updateContact,
  type Contact,
  type ContactCreate,
  type ContactPatch,
  type NotificationConsent,
  importContacts,
  type ImportResult,
} from '../api/contacts.js';
import { ApiError } from '../api/apiClient.js';
import { useUiStore } from '../state/uiStore.js';
import { t } from '../i18n/es.js';

/**
 * #127 P0 — Vista Contactos. Tabla + búsqueda + filtros + creación inline.
 * Paridad legacy BuilderHub.jsx:6418-6520 ContactsPanel.
 */
export function ContactsList(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const pushToast = useUiStore((s) => s.pushToast);
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const [consent, setConsent] = useState<NotificationConsent | ''>('');
  const [status, setStatus] = useState<Contact['status'] | 'all' | ''>('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Contact | null>(null);
  const [confirmingArchive, setConfirmingArchive] = useState<Contact | null>(null);

  const archiveMutation = useMutation({
    mutationFn: (c: Contact) => archiveContact(id, c.id),
    onSuccess: () => {
      pushToast('success', t.contact.archived);
      queryClient.invalidateQueries({ queryKey: ['contacts', id] });
      setConfirmingArchive(null);
    },
    onError: (e: unknown) => {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      pushToast('error', msg);
    },
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['contacts', id, { q, consent, status }],
    queryFn: () =>
      listContacts(id, {
        ...(q ? { q } : {}),
        ...(consent ? { notificationConsent: consent } : {}),
        ...(status ? { status } : {}),
      }),
    enabled: Boolean(id),
  });

  const [importe, setImporte] = useState<ImportResult | null>(null);
  const importMutation = useMutation({
    mutationFn: async (file: File) => importContacts(id, await file.text()),
    onSuccess: (res) => {
      setImporte(res);
      queryClient.invalidateQueries({ queryKey: ['contacts', id] });
    },
  });

  const items = data?.items ?? [];

  return (
    <div data-testid="contacts-list">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold">{t.contact.title}</h2>
        <div className="flex items-center gap-2">
          <label
            className="cursor-pointer rounded border border-slate-300 px-3 py-1 text-sm text-slate-700 hover:bg-slate-50"
            data-testid="contacts-import-label"
          >
            Importar CSV
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              data-testid="contacts-import-input"
              onChange={(e) => {
                const file = e.target.files?.[0];
                // Se limpia el input para que volver a elegir el mismo archivo
                // dispare el evento otra vez: reimportar tras corregir es el
                // caso normal.
                e.target.value = '';
                if (file) void importMutation.mutateAsync(file).catch(() => undefined);
              }}
            />
          </label>
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="rounded bg-brand-500 px-3 py-1 text-sm text-white hover:bg-brand-600"
            data-testid="contacts-new-btn"
          >
            + {t.contact.newBtn}
          </button>
        </div>
      </div>

      {importMutation.isPending && (
        <p className="mb-3 text-sm text-slate-500">Importando contactos…</p>
      )}
      {importMutation.isError && (
        <p role="alert" className="mb-3 rounded border border-red-300 bg-red-50 p-2 text-sm text-red-800">
          No se pudo importar: {(importMutation.error as Error)?.message}
        </p>
      )}
      {importe && (
        <div
          data-testid="import-result"
          className="mb-3 rounded border border-slate-300 bg-slate-50 p-2 text-sm"
        >
          <p>
            <strong>{importe.creados}</strong> creados ·{' '}
            <strong>{importe.actualizados}</strong> actualizados
            {importe.omitidos > 0 && <> · {importe.omitidos} omitidos (archivados)</>}
            {importe.duplicadosEnArchivo > 0 && (
              <> · {importe.duplicadosEnArchivo} repetidos en el archivo</>
            )}
          </p>
          {importe.errores.length > 0 && (
            <details className="mt-1">
              {/* Con la línea a la vista el operador corrige el archivo y
                  reimporta, que es lo que va a hacer igual. */}
              <summary className="cursor-pointer text-amber-800">
                {importe.errores.length} fila(s) no se pudieron importar
              </summary>
              <ul className="mt-1 list-inside list-disc text-xs text-slate-700">
                {importe.errores.slice(0, 20).map((e) => (
                  <li key={e.linea}>línea {e.linea}: {e.motivo}</li>
                ))}
              </ul>
              {importe.errores.length > 20 && (
                <p className="mt-1 text-xs text-slate-500">
                  …y {importe.errores.length - 20} más
                </p>
              )}
            </details>
          )}
          <button
            type="button"
            onClick={() => setImporte(null)}
            className="mt-1 text-xs text-slate-500 underline"
          >
            cerrar
          </button>
        </div>
      )}

      <div className="mb-3 flex flex-wrap gap-2">
        <input
          type="search"
          placeholder={t.contact.search}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="min-w-[280px] flex-1 rounded border border-slate-300 px-3 py-1 text-sm"
          data-testid="contacts-search"
        />
        <select
          value={consent}
          onChange={(e) => setConsent(e.target.value as NotificationConsent | '')}
          className="rounded border border-slate-300 px-2 py-1 text-sm"
          data-testid="contacts-consent-filter"
        >
          <option value="">{t.contact.consentLabel}: {t.contact.consentAll}</option>
          <option value="accepted">{t.contact.consentAccepted}</option>
          <option value="rejected">{t.contact.consentRejected}</option>
          <option value="unknown">{t.contact.consentUnknown}</option>
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as Contact['status'] | 'all' | '')}
          className="rounded border border-slate-300 px-2 py-1 text-sm"
          data-testid="contacts-status-filter"
        >
          <option value="">{t.contact.statusLabel}: {t.contact.statusActive}</option>
          <option value="active">{t.contact.statusActive}</option>
          <option value="blocked">{t.contact.statusBlocked}</option>
          <option value="archived">{t.contact.statusArchived}</option>
          <option value="all">{t.contact.statusAll}</option>
        </select>
      </div>

      {isLoading && <p className="text-slate-500">{t.app.loading}</p>}
      {error && <p className="text-red-700">{(error as Error).message}</p>}
      {!isLoading && !error && items.length === 0 && (
        <p className="rounded border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-slate-500">
          {t.contact.empty}
        </p>
      )}

      {items.length > 0 && (
        <div className="overflow-x-auto rounded border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2">{t.contact.cols.name}</th>
                <th className="px-3 py-2">{t.contact.cols.phone}</th>
                <th className="px-3 py-2">{t.contact.cols.email}</th>
                <th className="px-3 py-2">{t.contact.cols.channel}</th>
                <th className="px-3 py-2">{t.contact.cols.consent}</th>
                <th className="px-3 py-2">{t.contact.cols.status}</th>
                <th className="px-3 py-2">{t.contact.cols.lastContact}</th>
                <th className="px-3 py-2">{t.contact.cols.actions}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50" data-testid={`contact-row-${c.id}`}>
                  <td className="px-3 py-2 font-medium">{c.name}</td>
                  <td className="px-3 py-2 font-mono text-xs">{c.phone}</td>
                  <td className="px-3 py-2 text-slate-600">{c.email ?? '—'}</td>
                  <td className="px-3 py-2 text-slate-600">{c.preferredChannel ?? '—'}</td>
                  <td className="px-3 py-2">
                    <ConsentBadge consent={c.notificationConsent} />
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge status={c.status} />
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">
                    {c.lastContactAt ? new Date(c.lastContactAt).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <button
                      type="button"
                      onClick={() => setEditing(c)}
                      className="mr-1 rounded border border-slate-300 px-2 py-0.5 hover:bg-slate-50"
                      data-testid={`contact-edit-${c.id}`}
                    >
                      {t.contact.editBtn}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmingArchive(c)}
                      className="rounded border border-red-300 px-2 py-0.5 text-red-700 hover:bg-red-50"
                      data-testid={`contact-archive-${c.id}`}
                    >
                      {t.contact.archiveBtn}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <ContactFormModal
          tenantId={id}
          onClose={() => setShowForm(false)}
          onCreated={() => {
            pushToast('success', t.contact.created);
            queryClient.invalidateQueries({ queryKey: ['contacts', id] });
            setShowForm(false);
          }}
        />
      )}

      {editing && (
        <ContactEditModal
          tenantId={id}
          contact={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            pushToast('success', t.contact.updated);
            queryClient.invalidateQueries({ queryKey: ['contacts', id] });
            setEditing(null);
          }}
        />
      )}

      {confirmingArchive && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          data-testid="contact-archive-modal"
        >
          <div className="w-full max-w-sm rounded bg-white p-5 shadow-lg">
            <h3 className="mb-2 text-lg font-semibold">{t.contact.archiveBtn}</h3>
            <p className="mb-4 text-sm text-slate-600">{t.contact.confirmArchive}</p>
            <p className="mb-4 text-sm font-medium">{confirmingArchive.name} · {confirmingArchive.phone}</p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmingArchive(null)}
                className="rounded border border-slate-300 px-3 py-1 text-sm hover:bg-slate-100"
              >
                {t.contact.form.cancel}
              </button>
              <button
                type="button"
                onClick={() => archiveMutation.mutate(confirmingArchive)}
                disabled={archiveMutation.isPending}
                className="rounded bg-red-600 px-3 py-1 text-sm text-white hover:bg-red-700 disabled:opacity-50"
                data-testid="contact-archive-confirm"
              >
                {archiveMutation.isPending ? '…' : t.contact.archiveBtn}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface ContactEditModalProps {
  tenantId: string;
  contact: Contact;
  onClose: () => void;
  onSaved: () => void;
}

function ContactEditModal({ tenantId, contact, onClose, onSaved }: ContactEditModalProps): JSX.Element {
  const pushToast = useUiStore((s) => s.pushToast);
  const [name, setName] = useState(contact.name);
  const [phone, setPhone] = useState(contact.phone);
  const [email, setEmail] = useState(contact.email ?? '');
  const [preferredChannel, setPreferredChannel] = useState<Contact['preferredChannel'] | ''>(
    contact.preferredChannel ?? '',
  );
  const [consent, setConsent] = useState<NotificationConsent>(contact.notificationConsent);
  const [status, setStatus] = useState<Contact['status']>(contact.status);
  const [tagsStr, setTagsStr] = useState(contact.tags.join(', '));

  const mutation = useMutation({
    mutationFn: () => {
      const patch: ContactPatch = { expectedVersion: contact.version };
      if (name.trim() !== contact.name) patch.name = name.trim();
      if (phone.trim() !== contact.phone) patch.phone = phone.trim();
      const emailVal = email.trim();
      if (emailVal !== (contact.email ?? '')) patch.email = emailVal;
      if (preferredChannel !== (contact.preferredChannel ?? '')) {
        if (preferredChannel) patch.preferredChannel = preferredChannel;
      }
      if (consent !== contact.notificationConsent) patch.notificationConsent = consent;
      if (status !== contact.status) patch.status = status;
      const tags = tagsStr.split(',').map((s) => s.trim()).filter(Boolean);
      if (JSON.stringify(tags) !== JSON.stringify(contact.tags)) patch.tags = tags;
      return updateContact(tenantId, contact.id, patch);
    },
    onSuccess: onSaved,
    onError: (e: unknown) => {
      if (e instanceof ApiError) {
        if (e.status === 409) {
          pushToast('error', t.contact.conflict);
          return;
        }
      }
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      pushToast('error', msg);
    },
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="contact-edit-modal"
    >
      <form
        className="w-full max-w-md rounded bg-white p-5 shadow-lg"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        <h3 className="mb-3 text-lg font-semibold">{t.contact.form.edit}</h3>
        <div className="space-y-3 text-sm">
          <label className="block">
            <span className="mb-1 block font-medium text-slate-700">{t.contact.form.name} *</span>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded border border-slate-300 px-2 py-1"
            />
          </label>
          <label className="block">
            <span className="mb-1 block font-medium text-slate-700">{t.contact.form.phone} *</span>
            <input
              type="tel"
              required
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full rounded border border-slate-300 px-2 py-1 font-mono"
            />
          </label>
          <label className="block">
            <span className="mb-1 block font-medium text-slate-700">{t.contact.form.email}</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded border border-slate-300 px-2 py-1"
            />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="mb-1 block font-medium text-slate-700">{t.contact.form.preferredChannel}</span>
              <select
                value={preferredChannel}
                onChange={(e) => setPreferredChannel(e.target.value as Contact['preferredChannel'] | '')}
                className="w-full rounded border border-slate-300 px-2 py-1"
              >
                <option value="">—</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="webchat">Webchat</option>
                <option value="email">Email</option>
                <option value="sms">SMS</option>
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block font-medium text-slate-700">{t.contact.statusLabel}</span>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as Contact['status'])}
                className="w-full rounded border border-slate-300 px-2 py-1"
              >
                <option value="active">{t.contact.statusActive}</option>
                <option value="blocked">{t.contact.statusBlocked}</option>
                <option value="archived">{t.contact.statusArchived}</option>
              </select>
            </label>
          </div>
          <label className="block">
            <span className="mb-1 block font-medium text-slate-700">{t.contact.form.consent}</span>
            <select
              value={consent}
              onChange={(e) => setConsent(e.target.value as NotificationConsent)}
              className="w-full rounded border border-slate-300 px-2 py-1"
            >
              <option value="unknown">{t.contact.consentUnknown}</option>
              <option value="accepted">{t.contact.consentAccepted}</option>
              <option value="rejected">{t.contact.consentRejected}</option>
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block font-medium text-slate-700">{t.contact.form.tags}</span>
            <input
              type="text"
              value={tagsStr}
              onChange={(e) => setTagsStr(e.target.value)}
              className="w-full rounded border border-slate-300 px-2 py-1"
            />
          </label>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-slate-300 px-3 py-1 text-sm hover:bg-slate-100"
          >
            {t.contact.form.cancel}
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="rounded bg-brand-500 px-3 py-1 text-sm text-white hover:bg-brand-600 disabled:opacity-50"
            data-testid="contact-edit-submit"
          >
            {mutation.isPending ? '…' : t.contact.form.save}
          </button>
        </div>
      </form>
    </div>
  );
}

interface ContactFormModalProps {
  tenantId: string;
  onClose: () => void;
  onCreated: () => void;
}

function ContactFormModal({ tenantId, onClose, onCreated }: ContactFormModalProps): JSX.Element {
  const pushToast = useUiStore((s) => s.pushToast);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [preferredChannel, setPreferredChannel] = useState<Contact['preferredChannel'] | ''>('');
  const [consent, setConsent] = useState<NotificationConsent>('unknown');
  const [tagsStr, setTagsStr] = useState('');

  const mutation = useMutation({
    mutationFn: () => {
      const body: ContactCreate = {
        name: name.trim(),
        phone: phone.trim(),
        notificationConsent: consent,
      };
      if (email.trim()) body.email = email.trim();
      if (preferredChannel) body.preferredChannel = preferredChannel;
      const tags = tagsStr.split(',').map((s) => s.trim()).filter(Boolean);
      if (tags.length > 0) body.tags = tags;
      return createContact(tenantId, body);
    },
    onSuccess: onCreated,
    onError: (e: unknown) => {
      if (e instanceof ApiError && e.status === 409) {
        pushToast('error', t.contact.conflict);
        return;
      }
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      pushToast('error', msg);
    },
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="contact-form-modal"
    >
      <form
        className="w-full max-w-md rounded bg-white p-5 shadow-lg"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
        data-testid="contact-form"
      >
        <h3 className="mb-3 text-lg font-semibold">{t.contact.newBtn}</h3>
        <div className="space-y-3 text-sm">
          <label className="block">
            <span className="mb-1 block font-medium text-slate-700">{t.contact.form.name} *</span>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded border border-slate-300 px-2 py-1"
              data-testid="contact-form-name"
            />
          </label>
          <label className="block">
            <span className="mb-1 block font-medium text-slate-700">{t.contact.form.phone} *</span>
            <input
              type="tel"
              required
              placeholder="+56 9 1234 5678"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full rounded border border-slate-300 px-2 py-1 font-mono"
              data-testid="contact-form-phone"
            />
          </label>
          <label className="block">
            <span className="mb-1 block font-medium text-slate-700">{t.contact.form.email}</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded border border-slate-300 px-2 py-1"
              data-testid="contact-form-email"
            />
          </label>
          <label className="block">
            <span className="mb-1 block font-medium text-slate-700">{t.contact.form.preferredChannel}</span>
            <select
              value={preferredChannel}
              onChange={(e) => setPreferredChannel(e.target.value as Contact['preferredChannel'] | '')}
              className="w-full rounded border border-slate-300 px-2 py-1"
              data-testid="contact-form-channel"
            >
              <option value="">—</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="webchat">Webchat</option>
              <option value="email">Email</option>
              <option value="sms">SMS</option>
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block font-medium text-slate-700">{t.contact.form.consent}</span>
            <select
              value={consent}
              onChange={(e) => setConsent(e.target.value as NotificationConsent)}
              className="w-full rounded border border-slate-300 px-2 py-1"
              data-testid="contact-form-consent"
            >
              <option value="unknown">{t.contact.consentUnknown}</option>
              <option value="accepted">{t.contact.consentAccepted}</option>
              <option value="rejected">{t.contact.consentRejected}</option>
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block font-medium text-slate-700">{t.contact.form.tags}</span>
            <input
              type="text"
              value={tagsStr}
              onChange={(e) => setTagsStr(e.target.value)}
              placeholder="vip, banca, lima"
              className="w-full rounded border border-slate-300 px-2 py-1"
              data-testid="contact-form-tags"
            />
          </label>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-slate-300 px-3 py-1 text-sm hover:bg-slate-100"
          >
            {t.contact.form.cancel}
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="rounded bg-brand-500 px-3 py-1 text-sm text-white hover:bg-brand-600 disabled:opacity-50"
            data-testid="contact-form-submit"
          >
            {mutation.isPending ? '…' : t.contact.form.submit}
          </button>
        </div>
      </form>
    </div>
  );
}

const CONSENT_TONE: Record<NotificationConsent, string> = {
  accepted: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-800',
  unknown: 'bg-slate-100 text-slate-600',
};
function ConsentBadge({ consent }: { consent: NotificationConsent }): JSX.Element {
  return <span className={`rounded px-2 py-0.5 text-xs ${CONSENT_TONE[consent]}`}>{consent}</span>;
}

const STATUS_TONE: Record<Contact['status'], string> = {
  active: 'bg-emerald-100 text-emerald-800',
  blocked: 'bg-red-100 text-red-800',
  archived: 'bg-slate-100 text-slate-600',
};
function StatusBadge({ status }: { status: Contact['status'] }): JSX.Element {
  return <span className={`rounded px-2 py-0.5 text-xs ${STATUS_TONE[status]}`}>{status}</span>;
}
